const versionServiceLifecycleMixin = {
  /**
   * Initialize the version service
   */
  init() {
    if (this.initialized) {
      return;
    }

    // Skip auto-refresh in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('VersionService: Skipping auto-refresh in development mode');
      // Set a default version in development to avoid undefined issues
      this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
      this.initialized = true;
      return;
    }

    // Get current version from manifest or meta, unless a quiz attempt is active.
    if (!this.isActiveQuizFlow()) {
      this.getCurrentVersion().then(version => {
        this.currentVersion = version;
        console.log('VersionService initialized with version:', version);
      });
    }

    // Start periodic checking
    this.startPeriodicCheck();

    // Listen for visibility changes to check when user returns to tab
    this.visibilityHandler = () => {
      if (!document.hidden) {
        // Check immediately when user returns to tab
        setTimeout(() => {
          this.forceCheck();
        }, this.focusCheckDelay);
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Listen for online/offline events
    this.onlineHandler = () => {
      // Check immediately when coming back online
      setTimeout(() => {
        this.forceCheck();
      }, 1000);
    };
    window.addEventListener('online', this.onlineHandler);

    // Listen for page focus (when user clicks on window)
    this.focusHandler = () => {
      // Check immediately when window gains focus
      setTimeout(() => {
        this.forceCheck();
      }, this.focusCheckDelay);
    };
    window.addEventListener('focus', this.focusHandler);

    this.initialized = true;

    // Listen for route changes (React Router navigation)
    // This will be handled by the hook that uses this service
  },

  /**
   * Avoid update checks while a student is in the quiz-taking flow.
   */
  isActiveQuizFlow() {
    if (typeof window === 'undefined') {
      return false;
    }

    const pathname = window.location.pathname;
    const isQuizRoute = pathname.includes('/student/attempt/') ||
      pathname.includes('/student/result/') ||
      pathname.includes('/student/quizzes') ||
      (pathname.includes('/student/assignments/') && pathname.includes('/warning'));
    const hasActiveFlag = window.sessionStorage?.getItem('faithinsight_active_quiz_attempt') === 'true';
    const lastActiveAt = Number(window.sessionStorage?.getItem('faithinsight_quiz_flow_last_active_at') || 0);
    const isInGracePeriod = lastActiveAt > 0 && Date.now() - lastActiveAt < this.quizFlowGracePeriodMs;

    return isQuizRoute || hasActiveFlag || isInGracePeriod;
  },

  /**
   * Check if enough time has passed since last check
   */
  shouldCheck() {
    if (this.isActiveQuizFlow()) {
      return false;
    }

    const now = Date.now();
    return (now - this.lastCheckTime) > this.minCheckInterval;
  },

  /**
   * Start periodic version checking
   */
  startPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(() => {
      if (this.shouldCheck()) {
        this.checkForUpdates();
      }
    }, this.checkIntervalMs);
  },

  /**
   * Stop periodic checking
   */
  stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  },

  destroy() {
    this.stopPeriodicCheck();

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }

    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = null;
    }

    this.listeners = [];
    this.initialized = false;
  },

  /**
   * Check for updates
   */
  async checkForUpdates() {
    // Skip in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    if (this.isActiveQuizFlow()) {
      return;
    }

    if (this.isChecking) {
      return;
    }

    this.isChecking = true;
    this.lastCheckTime = Date.now();

    try {
      const latestVersion = await this.getCurrentVersion();

      // Debug logging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('Version check - Current:', this.currentVersion, 'Latest:', latestVersion);
      }

      if (this.currentVersion && latestVersion !== this.currentVersion) {
        console.log('🔄 New version detected:', latestVersion, 'Current:', this.currentVersion);
        this.notifyListeners('update-available', {
          currentVersion: this.currentVersion,
          latestVersion: latestVersion,
          timestamp: Date.now()
        });
      } else if (!this.currentVersion) {
        // First time checking - store the version
        this.currentVersion = latestVersion;
        if (process.env.NODE_ENV === 'development') {
          console.log('VersionService: Initial version set to:', latestVersion);
        }
      } else if (this.currentVersion && latestVersion === this.currentVersion) {
        if (process.env.NODE_ENV === 'development') {
          console.log('No updates available - versions match');
        }
      }
    } catch (error) {
      console.warn('Error checking for updates:', error);
    } finally {
      this.isChecking = false;
    }
  },

  /**
   * Force check for updates
   */
  async forceCheck() {
    // Skip in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    if (this.isActiveQuizFlow()) {
      return;
    }
    this.lastCheckTime = 0; // Reset last check time
    await this.checkForUpdates();
  },

  /**
   * Manual check for updates (works in development mode)
   */
  async manualCheck() {
    console.log('Manual version check triggered');
    // In development, just set a default version without fetching
    if (process.env.NODE_ENV === 'development') {
      if (!this.currentVersion) {
        this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
        console.log('VersionService: Manual check set version to:', this.currentVersion);
      }
      return;
    }
    await this.checkForUpdates();
  },

  /**
   * Add event listener
   */
  addListener(callback) {
    this.listeners.push(callback);
  },

  /**
   * Remove event listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  },

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in version service listener:', error);
      }
    });
  },
};

export default versionServiceLifecycleMixin;
